import {
  InventoryOpsPoDetail,
  InventoryOpsQcStatus,
  InventoryOpsRejectedSkuVariant,
} from "../../../../../../models/ProjectManagement/ProjectInventoryProduct";
import {
  InventoryOpsQcFields,
  InventoryOpsSkuVariantLine,
} from "../../../../../../models/ProjectManagement/InventoryOpsRequests";
import { SkuQtyRow } from "./SkuVariantQtyList";

export type InventoryOpsQcStage = "raw" | "print";

const normalizeSku = (sku: string) => sku.trim().toLowerCase();

/**
 * True when the API tracks passed qty for this QC, so pending is known.
 * Null / missing status (older API) and UNCONFIRMED fall back to the legacy
 * rejected-only rules.
 */
export const isQcStatusTracked = (
  status: InventoryOpsQcStatus | null | undefined,
): boolean =>
  status === "NOT_STARTED" ||
  status === "IN_PROGRESS" ||
  status === "COMPLETED";

export interface InventoryOpsQcSnapshot {
  status: InventoryOpsQcStatus | null;
  tracked: boolean;
  received: number;
  rejected: number;
  /** Null when passed qty is not tracked. */
  passed: number | null;
  /** Null when pending is unknown (UNCONFIRMED / untracked). */
  pending: number | null;
  /** Max qty that can still be tested (backend cap) on a qty inward. */
  checkable: number;
  /**
   * Qty is counted during QC (tested is added to received). Set by Mark
   * Received with count_at_qc: true (*_inward_qty_source = "QC").
   */
  countAtQc: boolean;
  /**
   * Qty inward with no qty yet. A tested save returns 400, so QC waits for
   * the /qty endpoint (or switching to count at QC).
   */
  awaitingQty: boolean;
  /**
   * Raw only: legacy QC (passed never recorded) whose stock is already at the
   * printer. A partial tested save would be rejected, so only Confirm QC
   * (+ rejects) is offered.
   */
  confirmOnly: boolean;
}

export const getQcSnapshot = (
  po: InventoryOpsPoDetail | null | undefined,
  stage: InventoryOpsQcStage,
): InventoryOpsQcSnapshot => {
  const isRaw = stage === "raw";
  const status =
    (isRaw ? po?.raw_inward_qc_status : po?.print_inward_qc_status) ?? null;
  const received =
    (isRaw ? po?.raw_inward_quantity : po?.print_inward_quantity) ?? 0;
  const rejected =
    (isRaw
      ? po?.raw_inward_rejection_quantity
      : po?.print_inward_rejection_quantity) ?? 0;
  const tracked = isQcStatusTracked(status);
  const passed = tracked
    ? ((isRaw
        ? po?.raw_inward_qc_passed_quantity
        : po?.print_inward_qc_passed_quantity) ?? 0)
    : null;
  const apiPending = isRaw
    ? po?.raw_inward_qc_pending_quantity
    : po?.print_inward_qc_pending_quantity;
  const pending = tracked
    ? (apiPending ?? Math.max(0, received - rejected - (passed ?? 0)))
    : null;

  const countAtQc =
    (isRaw ? po?.raw_inward_qty_source : po?.print_inward_qty_source) === "QC";
  const inwardId = isRaw ? po?.raw_inward_id : po?.print_inward_id;
  const awaitingQty = !countAtQc && Boolean(inwardId) && received <= 0;
  const confirmOnly =
    isRaw &&
    !countAtQc &&
    (status === "UNCONFIRMED" || status === "NOT_STARTED") &&
    po?.raw_inward_passed_quantity == null &&
    (po?.print_outward_quantity ?? 0) > 0;

  return {
    status,
    tracked,
    received,
    rejected,
    passed,
    pending,
    checkable: pending ?? Math.max(0, received - rejected),
    countAtQc,
    awaitingQty,
    confirmOnly,
  };
};

/** Passed qty for a QC variant row; null on legacy rows that never tracked it. */
export const getVariantPassedQty = (
  variant: InventoryOpsRejectedSkuVariant,
): number | null => {
  const value = variant.total_passed_quantity ?? variant.passed_quantity;
  if (value == null || Number.isNaN(Number(value))) return null;
  return Number(value);
};

/**
 * Passed qty by SKU. SKUs without a QC row are absent (caller treats that as
 * 0 passed); SKUs with a legacy QC row map to null.
 */
export const getPassedQtyBySku = (
  variants: InventoryOpsRejectedSkuVariant[] | null | undefined,
): Map<string, number | null> => {
  const map = new Map<string, number | null>();
  (variants || []).forEach((variant) => {
    if (!variant.sku) return;
    map.set(normalizeSku(variant.sku), getVariantPassedQty(variant));
  });
  return map;
};

const findSkuLine = (
  row: SkuQtyRow,
  skuLines: InventoryOpsSkuVariantLine[] | null | undefined,
) =>
  skuLines?.find(
    (line) => line.sku === row.sku || line.variant_id === row.variant_id,
  );

/** Selected SKU lines that carry a tested and/or rejected qty. */
export const buildQcSkuLines = (
  rows: SkuQtyRow[],
  skuLines: InventoryOpsSkuVariantLine[] | null | undefined,
  selectedSkus: Set<string>,
): InventoryOpsSkuVariantLine[] =>
  rows
    .filter((row) => selectedSkus.has(row.sku))
    .map((row) => {
      const entered = findSkuLine(row, skuLines);
      const tested = entered?.tested_quantity ?? 0;
      return {
        variant_id: row.variant_id,
        sku: row.sku,
        title: row.title ?? null,
        quantity: entered?.quantity ?? 0,
        ...(tested > 0 ? { tested_quantity: tested } : {}),
      };
    })
    .filter((line) => line.quantity > 0 || (line.tested_quantity ?? 0) > 0);

export type QcRequestFieldsResult =
  { ok: true; fields: InventoryOpsQcFields } | { ok: false; message: string };

const validateTestedLine = (
  name: string,
  tested: number,
  rejected: number,
  countAtQc: boolean,
): string | null => {
  if (countAtQc && tested <= 0) {
    return `Enter tested quantity for ${name}.`;
  }
  if (tested > 0 && rejected > tested) {
    return `Rejected cannot exceed tested for ${name}.`;
  }
  return null;
};

/**
 * Builds the qty part of a raw / print QC request.
 * - Save: tested and/or rejected qty (per selected SKU or product level).
 *   Rejected must be ≤ tested; a reject-only line (no tested) is allowed on
 *   qty inwards. Count at QC needs tested on every line.
 * - passRemaining ("Pass rest & close QC" / "Confirm QC", qty inwards only):
 *   entered rejections are optional; the API passes everything unchecked.
 */
export const buildQcRequestFields = ({
  rows,
  skuLines,
  selectedSkus,
  productRejected,
  productTested,
  checkableQty,
  passRemaining,
  countAtQc = false,
}: {
  rows: SkuQtyRow[];
  skuLines: InventoryOpsSkuVariantLine[] | null | undefined;
  selectedSkus: Set<string>;
  productRejected: number | null | undefined;
  productTested: number | null | undefined;
  /**
   * Product-level cap: unchecked qty on a qty inward, or qty still to receive
   * when counting at QC.
   */
  checkableQty: number;
  passRemaining?: boolean;
  countAtQc?: boolean;
}): QcRequestFieldsResult => {
  if (passRemaining && countAtQc) {
    return {
      ok: false,
      message: "Counted at QC: enter tested quantity instead.",
    };
  }

  if (rows.length > 0) {
    const lines = buildQcSkuLines(rows, skuLines, selectedSkus);
    for (const line of lines) {
      const error = validateTestedLine(
        line.title || line.sku,
        line.tested_quantity ?? 0,
        line.quantity,
        countAtQc,
      );
      if (error) return { ok: false, message: error };
    }

    if (passRemaining) {
      const hasUnchecked = rows.some((row) => (row.maxQty ?? 0) > 0);
      if (!hasUnchecked) {
        return { ok: false, message: "No unchecked quantity left to pass." };
      }
      return {
        ok: true,
        fields: {
          ...(lines.length > 0 ? { sku_variants: lines } : {}),
          pass_remaining: true,
        },
      };
    }
    if (selectedSkus.size === 0) {
      return { ok: false, message: "Select at least one SKU to record QC." };
    }
    if (lines.length === 0) {
      return {
        ok: false,
        message: countAtQc
          ? "Enter tested quantity for at least one selected SKU."
          : "Enter tested or rejected quantity for at least one selected SKU.",
      };
    }
    return { ok: true, fields: { sku_variants: lines } };
  }

  const rejected = productRejected ?? 0;
  const tested = passRemaining ? 0 : (productTested ?? 0);

  if (passRemaining) {
    if (checkableQty <= 0) {
      return { ok: false, message: "No unchecked quantity left to pass." };
    }
    if (rejected > checkableQty) {
      return {
        ok: false,
        message: `Rejected cannot exceed unchecked quantity (${checkableQty}).`,
      };
    }
    return {
      ok: true,
      fields: {
        ...(rejected > 0 ? { quantity: rejected } : {}),
        pass_remaining: true,
      },
    };
  }

  if (tested <= 0 && (countAtQc || rejected <= 0)) {
    return {
      ok: false,
      message: countAtQc
        ? "Enter tested quantity greater than 0."
        : "Enter tested or rejected quantity greater than 0.",
    };
  }
  const lineError = validateTestedLine("this product", tested, rejected, false);
  if (lineError) return { ok: false, message: lineError };

  const counted = tested > 0 ? tested : rejected;
  if (counted > checkableQty) {
    return {
      ok: false,
      message: countAtQc
        ? `Tested cannot exceed quantity still to receive (${checkableQty}).`
        : `${tested > 0 ? "Tested" : "Rejected"} cannot exceed unchecked quantity (${checkableQty}).`,
    };
  }
  return {
    ok: true,
    fields: {
      quantity: rejected,
      ...(tested > 0 ? { tested_quantity: tested } : {}),
    },
  };
};
